import { ADAPTER_TYPES } from '@/core/types/adapter.js';
import { DECISION_CATEGORIES } from '@/planning/decision-packet.js';
import { DECISION_PAUSE_UI_NOTES } from '@/adapters/shared/decision-pause-ui-shim.js';

export const MANAGED_HEADER =
  '<!-- managed by paqad-ai — do not edit; regenerated by `paqad refresh --providers` -->';

/**
 * Builds the canonical Decision Pause Contract BODY (without the managed header).
 * Sources the category list from `DECISION_CATEGORIES` and the per-adapter UI
 * notes from the shim so the contract cannot drift from the runtime.
 *
 * This body is the single source of truth for the contract: the framework
 * bootstrap (`runtime/AGENT-BOOTSTRAP.md`, assembled by
 * `src/onboarding/agent-bootstrap-writer.ts`) inlines it — including the
 * per-adapter UI table whose `claude-code` row is the instruction that keeps the
 * Claude Code decision "tray" (`AskUserQuestion`) firing. The contract ships
 * inside the framework install and is loaded from there; it is no longer copied
 * into every project's `.paqad/` (issue #229).
 */
export function buildDecisionPauseContractBody(): string {
  const categoriesList = DECISION_CATEGORIES.map((category) => `- \`${category}\``).join('\n');
  const adapterRows = ADAPTER_TYPES.map(
    (adapter) => `| \`${adapter}\` | ${DECISION_PAUSE_UI_NOTES[adapter]} |`,
  ).join('\n');

  return `# Decision Pause Contract

Before implementing any choice that falls into one of the categories below, write a Decision Packet to \`.paqad/decisions/pending/D-{id}.json\` and stop work. Do not continue until \`.paqad/decisions/resolved/D-{id}.json\` exists. \`{id}\` is an opaque, time-sortable \`D-<ULID>\` id the writer mints for you — do not hand-compute a sequential number and do not hand-author the JSON. Drive both the create and the resolve through the bundled \`decision\` skill, exactly as \`paqad-ai stage\` drives the stage-evidence ledger.

**Create packets only through the writer — never by hand.** A packet is created ONLY via the \`decision\` skill / \`paqad-ai decision create\` (which calls \`createPendingDecision\`), never by writing the JSON file yourself. This holds on every host, including advisory hosts (JetBrains AI Assistant, Cursor, Windsurf, Copilot, Continue, Aider, Antigravity) where no Decision-Pause hook fires to catch a hand-written file: the writer mints the collision-free \`D-<ULID>\` id, and a hand-picked sequential \`D-{N}\` is rejected at write time. Hand-authoring a \`D-1.json\` / \`D-4.json\` reintroduces the id collisions issue #184 fixed — do not do it.

## Categories

${categoriesList}

## Evidence-armed pauses (issue #361)

The categories are unchanged, but a \`create-vs-reuse\` pause no longer waits for the agent to volunteer that it is at a reuse fork. When \`decision_arm_mode\` is \`strict\`, the framework mints that packet from **evidence** on its own:

- **Plan time:** at \`plan compile\`, each declared \`new_constructs[]\` entry is scored against the code-knowledge index; a name/spec similarity at or above \`decision_arm_plan_threshold\` (default 0.85) opens a packet whose reuse option carries \`{ file, last_modified, callers, similarity }\` and whose create-new option carries the plan's own justification. The recommendation is reuse once the existing symbol has ≥ 3 callers.
- **Change time:** a blocking-band duplication finding (issue #358) opens the same packet from the finding's evidence.

Both mints go through \`paqad-ai decision create\` — never hand-authored — carry \`origin: "evidence-armed"\`, and are capped at \`decision_arm_max_per_change\` (default 1, the strongest fork only). An identical fork already answered in \`.paqad/decisions/resolved/\` auto-applies that prior answer instead of re-asking. Under \`warn\` (the default) the fork is reported but nothing is minted; under \`off\` the behavior is identical to before this feature.

## Resolution flow

1. Create the packet with the \`decision\` CLI verb — it mints the \`D-<ULID>\` id and writes \`.paqad/decisions/pending/D-{id}.json\` for you (resolved from the installed package on every onboarded project, so it never ENOENTs like a repo-local script):
   \`npx paqad-ai decision create --category <category> --title <title> --context <context> --option <key>=<label> --option <key>=<label> [--recommendation <key>]\`. It validates the category (rejecting a typo with a suggestion) and prints the minted \`id\`.
2. Present the packet's options to the user via the host's interactive UI (see the per-adapter table below). If multiple packets are pending, ask them one at a time in creation order (ids sort chronologically). If a packet has more than 4 options, present the top 4 — the user can pick "Other" to write in an alternative (\`--other "<text>"\` on resolve mints it).
3. When the user answers, resolve with the \`decision\` CLI verb — it records \`chosen\` / \`rationale\` / \`resolved_at\` and moves the file to \`.paqad/decisions/resolved/D-{id}.json\`:
   \`npx paqad-ai decision resolve <id> <chosen> [rationale]\` (or \`--other "<text>"\` for a write-in). A hand-picked sequential \`D-{N}\` is rejected, so parallel branches never collide.
4. Only after the resolved file exists may implementation continue. Commit the resolved packet with the change it justifies (the delivery workflow), so a reviewer and future \`git blame\` can see why.

## Per-adapter UI

Use the row that matches the \`Adapter:\` value in the entry file that pointed you to this bootstrap.

| Adapter | UI primitive |
| --- | --- |
${adapterRows}

## Fallback

If the interactive UI is not available (non-interactive run, hook context, etc.), stop work and wait until \`.paqad/decisions/resolved/D-{id}.json\` exists — created out of band by the user.`;
}

/**
 * The Decision Pause Contract as a standalone managed document (header + body).
 * Kept for callers/tests that want the document form; the project-level
 * `.paqad/` copy is no longer written (issue #229).
 */
export function buildDecisionPauseContractDocument(): string {
  return `${MANAGED_HEADER}\n\n${buildDecisionPauseContractBody()}\n`;
}
